import React, { useState, useCallback } from 'react';
import { accountingAPI } from '../../../services/api';
import { PageHeader }    from '../../../components/layout/PageHeader';
import { ArrowDownTrayIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const fmt = n => '₹' + Number(n||0).toLocaleString('en-IN', { minimumFractionDigits:2 });

const TYPE_LABEL = { sale:'Sales', purchase:'Purchase', expense:'Expense', indirect_income:'Other Income', proforma:'Proforma', delivery_challan:'Delivery Challan', debit_note:'Debit Note' };
const GST_SECTION = { sale:'Output GST (GSTR-1)', purchase:'Input GST (GSTR-2)', expense:'Input GST (GSTR-2)', indirect_income:'Output GST (GSTR-1)', debit_note:'Input GST', proforma:'Proforma (Draft)' };

export function GSTReport() {
  const thisYear = new Date().getFullYear();
  const [start, setStart]   = useState(`${thisYear}-04-01`);
  const [end,   setEnd]     = useState(`${thisYear}-03-31`);
  const [month, setMonth]   = useState('');
  const [data,  setData]    = useState(null);
  const [loading, setLoad]  = useState(false);

  const QUARTERS = [
    { label:'Q1 (Apr–Jun)', s:`${thisYear}-04-01`, e:`${thisYear}-06-30` },
    { label:'Q2 (Jul–Sep)', s:`${thisYear}-07-01`, e:`${thisYear}-09-30` },
    { label:'Q3 (Oct–Dec)', s:`${thisYear}-10-01`, e:`${thisYear}-12-31` },
    { label:'Q4 (Jan–Mar)', s:`${thisYear+1}-01-01`, e:`${thisYear+1}-03-31` },
  ];

  const load = useCallback(async () => {
    const s = month ? month + '-01' : start;
    const e = month ? month + '-' + new Date(Number(month.split('-')[0]), Number(month.split('-')[1]),0).getDate() : end;
    setLoad(true);
    try {
      const res = await accountingAPI.getGSTSummary({ startDate: s, endDate: e });
      setData(res.data);
    } catch { toast.error('Failed to load GST report'); }
    finally { setLoad(false); }
  }, [start, end, month]);

  // Compute output and input GST totals
  const outputTypes = ['sale','indirect_income'];
  const inputTypes  = ['purchase','expense','debit_note'];

  const totalsMap = {};
  (data?.totals||[]).forEach(t => { totalsMap[t._id] = t; });

  const outputTax = outputTypes.reduce((s,t) => s + (totalsMap[t]?.taxAmt||0), 0);
  const inputTax  = inputTypes.reduce((s,t)  => s + (totalsMap[t]?.taxAmt||0), 0);
  const netGST    = outputTax - inputTax;

  // Group gstData by type then taxRate
  const byType = {};
  (data?.gstData||[]).forEach(g => {
    const t = g._id.type;
    if (!byType[t]) byType[t] = [];
    byType[t].push(g);
  });

  return (
    <div className="space-y-5">
      <PageHeader title="GST Report" subtitle="Output tax, input tax credit and net GST liability summary" />

      {/* Controls */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex gap-4 flex-wrap items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">From Date</label>
            <input type="date" value={start} onChange={e=>{setStart(e.target.value);setMonth('');}}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">To Date</label>
            <input type="date" value={end} onChange={e=>{setEnd(e.target.value);setMonth('');}}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Or Select Month</label>
            <input type="month" value={month} onChange={e=>setMonth(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
          </div>
          <div className="flex gap-2 flex-wrap">
            {QUARTERS.map(q=>(
              <button key={q.label} onClick={()=>{setStart(q.s);setEnd(q.e);setMonth('');}}
                className="px-3 py-2 text-xs font-medium border border-gray-200 rounded-xl hover:bg-indigo-50 hover:border-indigo-300 text-gray-600 transition-colors">
                {q.label}
              </button>
            ))}
          </div>
          <button onClick={load} disabled={loading}
            className="px-5 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50">
            {loading ? 'Loading...' : 'Generate'}
          </button>
          {data && (
            <button onClick={()=>window.print()} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
              <ArrowDownTrayIcon className="w-4 h-4"/> Print / PDF
            </button>
          )}
        </div>
      </div>

      {!data && !loading && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
          <DocumentChartBarIcon className="w-12 h-12 mx-auto text-gray-200 mb-3"/>
          <p className="text-gray-400 text-sm">Select period and click Generate</p>
        </div>
      )}

      {data && (
        <>
          {/* Summary KPIs */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
              <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">Output GST Collected</p>
              <p className="text-2xl font-bold text-green-700">{fmt(outputTax)}</p>
              <p className="text-xs text-green-500 mt-1">GST collected on sales</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Input Tax Credit (ITC)</p>
              <p className="text-2xl font-bold text-blue-700">{fmt(inputTax)}</p>
              <p className="text-xs text-blue-500 mt-1">GST paid on purchases</p>
            </div>
            <div className={`rounded-2xl p-5 border ${netGST>=0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
              <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${netGST>=0?'text-red-600':'text-green-600'}`}>
                Net GST {netGST>=0?'Payable':'Refundable'}
              </p>
              <p className={`text-2xl font-bold ${netGST>=0?'text-red-700':'text-green-700'}`}>{fmt(Math.abs(netGST))}</p>
              <p className={`text-xs mt-1 ${netGST>=0?'text-red-500':'text-green-500'}`}>
                {netGST>=0 ? 'Amount to pay government' : 'Eligible for refund'}
              </p>
            </div>
          </div>

          {/* Output GST */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 px-6 py-4 bg-green-50">
              <h3 className="font-bold text-green-800 text-sm">Output GST — GSTR-1 (Sales)</h3>
            </div>
            <GSTTable types={outputTypes} byType={byType} totalsMap={totalsMap} />
          </div>

          {/* Input GST */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 px-6 py-4 bg-blue-50">
              <h3 className="font-bold text-blue-800 text-sm">Input Tax Credit — GSTR-2 (Purchases & Expenses)</h3>
            </div>
            <GSTTable types={inputTypes} byType={byType} totalsMap={totalsMap} />
          </div>

          {/* Net summary row */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 px-6 py-4 bg-gray-50">
              <h3 className="font-bold text-gray-800 text-sm">Net GST Liability Summary</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>{['Description','Taxable Amount','GST Amount'].map(h=>(
                  <th key={h} className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase text-right first:text-left">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                <tr>
                  <td className="px-4 py-2.5 font-medium text-green-700">Output GST (on Sales)</td>
                  <td className="px-4 py-2.5 text-right">{fmt(outputTypes.reduce((s,t)=>s+(totalsMap[t]?.taxableAmt||0),0))}</td>
                  <td className="px-4 py-2.5 text-right text-green-700 font-semibold">{fmt(outputTax)}</td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5 font-medium text-blue-700">Input Tax Credit (on Purchases)</td>
                  <td className="px-4 py-2.5 text-right">{fmt(inputTypes.reduce((s,t)=>s+(totalsMap[t]?.taxableAmt||0),0))}</td>
                  <td className="px-4 py-2.5 text-right text-blue-700 font-semibold">({fmt(inputTax)})</td>
                </tr>
                <tr className="bg-gray-50 font-bold border-t-2 border-gray-300">
                  <td className="px-4 py-2.5 text-gray-900">Net GST {netGST>=0?'Payable':'Refundable'}</td>
                  <td className="px-4 py-2.5 text-right">—</td>
                  <td className={`px-4 py-2.5 text-right text-lg ${netGST>=0?'text-red-600':'text-green-600'}`}>{fmt(Math.abs(netGST))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function GSTTable({ types, byType, totalsMap }) {
  const TYPE_LABEL = { sale:'Sales', purchase:'Purchases', expense:'Expenses', indirect_income:'Other Income', debit_note:'Debit Notes' };
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50">
        <tr>{['Document Type','Tax Rate','Taxable Amount','GST Amount','Transactions'].map(h=>(
          <th key={h} className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase text-right first:text-left">{h}</th>
        ))}</tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {types.map(t => {
          const rows = byType[t] || [];
          if (rows.length === 0) return null;
          return rows.map((row, i) => (
            <tr key={t+i} className="hover:bg-gray-50">
              {i===0 && <td className="px-4 py-2.5 font-medium text-gray-800" rowSpan={rows.length}>{TYPE_LABEL[t]||t}</td>}
              <td className="px-4 py-2.5 text-right text-gray-600">{row._id.taxRate}%</td>
              <td className="px-4 py-2.5 text-right text-gray-800">{fmt(row.taxableAmt)}</td>
              <td className="px-4 py-2.5 text-right font-semibold text-gray-900">{fmt(row.taxAmt)}</td>
              <td className="px-4 py-2.5 text-right text-gray-500">{row.count}</td>
            </tr>
          ));
        })}
        {/* Type subtotals */}
        {types.map(t => totalsMap[t] ? (
          <tr key={'sub-'+t} className="bg-gray-50 font-semibold">
            <td className="px-4 py-2 text-gray-700 pl-8">↳ {(TYPE_LABEL[t]||t)} Total</td>
            <td className="px-4 py-2 text-right text-gray-500">—</td>
            <td className="px-4 py-2 text-right">{fmt(totalsMap[t].taxableAmt)}</td>
            <td className="px-4 py-2 text-right text-gray-900">{fmt(totalsMap[t].taxAmt)}</td>
            <td className="px-4 py-2 text-right text-gray-500">{totalsMap[t].count}</td>
          </tr>
        ) : null)}
      </tbody>
    </table>
  );
}